import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './IntroductionPopup.module.scss';
import { COOKIE_FOR_WEEK, getCookie, setCookie, addDay } from '@/utils/date';

const cx = classNames.bind(styles);

const questions = [
  {
    question: `몇 명부터 단독 여행으로 진행되나요?`,
    answer: `상품별로 최소 예약 인원이 모두 다릅니다.
해당 인원 이상 예약 시 단독 여행으로 진행이 가능하며, 자세한 조건은 상담 시 안내해 드립니다.`,
  },
  {
    question: `여행 일정 변경이 가능한가요?`,
    answer: `상품에 따라 일정 변경 가능 여부가 다릅니다.
예를 들어 쇼핑센터 방문 제외, 선택 관광 일정 추가, 특정 관광지 방문 일정 추가 또는 제외 등이 이에 해당합니다.
원하시는 변경 사항을 알려주시면 담당자가 확인 후 가능 여부를 안내해 드립니다.`,
  },
  {
    question: `자유여행 시간을 추가할 수 있나요?`,
    answer: `상품에 따라 자유 일정 추가 가능 여부가 다릅니다.
자유여행 시간을 원하시는 경우, 요청 사항을 전달해 주시면 가능 여부를 확인해 드립니다.`,
  },
  {
    question: `저녁 만찬, 워크숍 등 단독 프로그램도 진행할 수 있나요?`,
    answer: `여행 지역에 따라 가능 여부가 달라집니다.
진행을 원하시는 단독 프로그램이 있다면 요청 내용을 전달해 주세요. 담당자가 가능 여부를 확인해 드립니다.`,
  },
  {
    question: `인솔자가 동행할 수 있나요?`,
    answer: `여행 지역 및 인원 수에 따라 인솔자 동행 여부가 결정됩니다.
인솔자 동행을 원하시는 경우, 관련 내용을 알려주시면 확인 후 안내해 드리겠습니다.`,
  },
  {
    question: `여행자보험은 포함되어 있나요?`,
    answer: `기본적으로 여행자보험이 포함되어 있습니다.
다만, 상품별로 가입 가능한 보험 종류에 따라 일부 제한이 있을 수 있으니 상담 시 꼭 확인해 주세요.`,
  },
  {
    question: `견적서는 언제 받을 수 있나요?`,
    answer: `상담이 완료된 후, 영업시간 기준으로 최대 48시간 이내에 여행사로부터 견적서를 받아보실 수 있습니다.
빠른 처리를 위해 신속하게 준비해 드리고 있습니다.`,
  },
  {
    question: `선택한 패키지와 동일한 가격으로 예약이 되나요?`,
    answer: `상품 가격은 여행 인원 수, 일정, 구성 변경 여부 등에 따라 달라질 수 있습니다.
선택한 상품과 동일한 가격이 적용되지 않을 수 있으며, 자세한 내용은 담당자와 상담 후 확인해 주세요.`,
  },
  {
    question: `취소료 규정은 일반 패키지와 동일한가요?`,
    answer: `취소료 규정은 일반 패키지 상품과 다르게 적용되는 경우가 많습니다.
정확한 내용은 '상품 상세 정보 > 취소 및 환불 규정'을 통해 확인해 주세요.`,
  },
];

const Dialog = ({ children, innerRef }: { children: React.ReactNode; innerRef?: React.RefObject<HTMLDivElement> }) => {
  return (
    <div className={cx('Dialog')} role="dialog" aria-modal="true">
      <div className={cx('inner')} ref={innerRef}>
        {children}
        <button type="button" className={cx('handle')} aria-label="레이어 내리기" />
      </div>
    </div>
  );
};

const IntroductionPopup: React.FC<{ closePopup: () => void }> = ({ closePopup }) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const [isChecked, setChecked] = useState(false);

  const closePopupForWeek = () => {
    setCookie(COOKIE_FOR_WEEK, 1, addDay(new Date(), 7), window.location.hostname, '/');
  };

  const savedCookie = () => {
    return getCookie(document.cookie, COOKIE_FOR_WEEK);
  };

  const onCloseClick = () => {
    if (isChecked) {
      closePopupForWeek();
    }
    closePopup();
  };

  // off-click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (innerRef.current && !innerRef.current.contains(e.target as Node)) {
        closePopup();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Dialog innerRef={innerRef}>
      <div className={cx('IntroductionPopup')}>
        <div className={cx('head')}>
          <div className={cx('inner')}>
            {!savedCookie() && (
              <button
                type="button"
                role="checkbox"
                className={cx('week')}
                onClick={() => setChecked((prev) => !prev)}
                aria-checked={isChecked}
              >
                일주일간 보지 않기
              </button>
            )}
            <button type="button" className={cx('close')} onClick={onCloseClick} aria-label="닫기" />
          </div>
        </div>
        <div className={cx('content')}>
          <section className={cx('introduction')}>
            <div className={cx('inner')}>
              <em className={cx('title')}>
                <div className={cx('hidden')}>
                  <b>단체여행 신청</b>
                  <br />
                  서비스란?
                </div>
              </em>
              <div className={cx('description')}>
                고객 맞춤형 단독 여행 서비스 입니다. 가족, 친구, 동료와 함께 원하는 구성으로 특별한 여행을 떠나보세요.
              </div>
              <button type="button" className={cx('apply')} onClick={closePopup}>
                단체여행 신청하기
              </button>
            </div>
          </section>
          <section className={cx('process')}>
            <div className={cx('inner')}>
              <div className={cx('title')}>
                <div className={cx('hidden')}>
                  신청·상담
                  <br />
                  진행 프로세스
                </div>
              </div>
              <ol>
                <li>
                  <div className={cx('title')}>
                    <span className={cx('hidden')}>01</span>
                    단체 여행 상담 신청
                  </div>
                  <div className={cx('description')}>
                    선택한 상품을 바탕으로 여행 유형, 일정, 인원, 예산 등을 정해 상담을 신청하세요. 계획이 없더라도 여행
                    어드바이저와 상담하며 단체 여행을 준비할 수 있습니다.
                  </div>
                  <div className={cx('annotation')}>
                    <b>여행 어드바이저란?</b>
                    <span>
                      여행 상담 전, 필요한 정보를 확인하고 고객의 여행 스타일과 요구 사항을 확인하는 역할을 합니다.
                    </span>
                  </div>
                </li>
                <li>
                  <div className={cx('title')}>
                    <span className={cx('hidden')}>02</span>
                    여행사 담당자 지정 및 상담 진행
                  </div>
                  <div className={cx('description')}>지역별 전문 담당자가 배정되어 맞춤 상담을 진행합니다.</div>
                </li>
                <li>
                  <div className={cx('title')}>
                    <span className={cx('hidden')}>03</span>
                    견적서 확인
                  </div>
                  <div className={cx('description')}>
                    상담 완료 후, 48시간 내 요청한 조건에 맞춘 여행 견적서를 받아보고 비교해보세요.
                  </div>
                </li>
                <li>
                  <div className={cx('title')}>
                    <span className={cx('hidden')}>04</span>
                    상품 확인
                  </div>
                  <div className={cx('description')}>
                    고객의 요청 사항과 확정된 견적서를 반영한 여행 상품을 전달드립니다. 세부 내용을 확인해보세요
                  </div>
                </li>
                <li>
                  <div className={cx('title')}>
                    <span className={cx('hidden')}>05</span>
                    상품 예약 및 결제
                  </div>
                  <div className={cx('description')}>
                    전달 받은 여행 상품을 예약하고 안전하게 결제하여 여행 준비를 완료하세요.
                  </div>
                </li>
              </ol>
            </div>
          </section>
          <section className={cx('question')}>
            <div className={cx('inner')}>
              <div className={cx('box')}>
                <div className={cx('title')}>FAQ</div>
                {questions.map(({ question, answer }, index) => (
                  <details>
                    <summary>{question}</summary>
                    <div className={cx('answer')}>
                      <span>{answer}</span>
                    </div>
                  </details>
                ))}
              </div>
              <div className={cx('box')}>
                <div className={cx('title')}>유의 및 안내사항</div>
                <div className={cx('description')}>
                  단체여행 신청 및 진행 관련 안내는 <b>네이버 톡톡</b>으로 발송됩니다.
                  <br />
                  <b>원활한 안내 수신을 위해 톡톡 알림을 꼭 설정해 주세요.</b>
                </div>
                <div className={cx('howto')}>
                  <div className={cx('title')}>톡톡 알림 수신 설정 방법</div>
                  <ol>
                    <li>
                      <span className={cx('step')}>Step 1</span>
                      네이버 앱 실행
                    </li>
                    <li>
                      <span className={cx('step')}>Step 2</span>
                      오른쪽 상단{' '}
                      <b>
                        종(
                        <i className={cx('bell')} />
                        )아이콘 클릭
                      </b>
                    </li>
                    <li>
                      <span className={cx('step')}>Step 3</span>상단{' '}
                      <b>
                        톱니바퀴(
                        <i className={cx('gear')} />) 아이콘 클릭 &rarr; 알림설정
                      </b>
                    </li>
                  </ol>
                  <div className={cx('title')}>아래 3가지 항목을 모두 푸시 알림 ON으로 설정해 주세요.</div>
                  <ul>
                    <li>전체 푸시 알림 ON</li>
                    <li>
                      서비스별 설정 &gt; <b>톡톡</b>
                    </li>
                    <li>
                      서비스별 설정 &gt; <b>네이버여행상품</b>
                    </li>
                  </ul>
                </div>
                <div className={cx('description')}>
                  단체여행 신청 및 견적 확정 이후에는,{' '}
                  <b>여행사에서 별도로 전달받은 상품을 통해 예약을 진행해 주세요.</b>
                </div>
                <div className={cx('description')}>
                  예약이 확정된 이후에는 <b>일정 변경 또는 취소 시 수수료가 발생할 수 있으므로</b>, 관련 내용은 반드시{' '}
                  <b>'상품 상세 정보 &gt; 취소 및 환불 규정'을 확인해 주세요.</b>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Dialog>
  );
};

export default IntroductionPopup;
